import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Divider,
} from '@mui/material';
import { X as CloseIcon, Trash as TrashIcon } from 'lucide-react';
import { useCartStore } from '../store/cartStore';

interface CartProps {
  open: boolean;
  onClose: () => void;
}

const Cart: React.FC<CartProps> = ({ open, onClose }) => {
  const { items, removeItem, updateQuantity, clearCart, getTotalPrice } = useCartStore();

  const handleQuantityChange = (productId: string, value: string) => {
    const quantity = parseInt(value, 10);
    if (!isNaN(quantity) && quantity >= 0) {
      updateQuantity(productId, quantity);
    }
  };

  const calculateProductPrice = (product: any, selectedTimePrice?: any) => {
    const basePrice = selectedTimePrice?.price || product.price;
    if (product.discountPercentage) {
      return basePrice * (1 - product.discountPercentage / 100);
    }
    return basePrice;
  };

  const handleCheckout = () => {
    const phoneNumber = "34632220854";
    let message = "Estoy interesado en comprar:\n\n";
    
    items.forEach((item) => {
      const price = calculateProductPrice(item.product, item.selectedTimePrice);
      const totalItemPrice = price * item.quantity;
      
      message += `• ${item.product.name}\n`;
      message += `  Cantidad: ${item.quantity}\n`;
      if (item.selectedTimePrice) {
        message += `  Plan: ${item.selectedTimePrice.time}\n`;
      }
      message += `  Precio: ${totalItemPrice.toFixed(2)}€\n\n`;
    });

    message += `\nTotal: ${getTotalPrice().toFixed(2)}€`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    onClose();
    clearCart();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Carrito de Compra</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {items.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ my: 4 }}>
            El carrito está vacío
          </Typography>
        ) : (
          <>
            <List>
              {items.map((item) => {
                const primaryImage = item.product.images.find((img) => img.isPrimary);
                const price = calculateProductPrice(item.product, item.selectedTimePrice);
                
                return (
                  <React.Fragment key={`${item.product.id}-${item.selectedTimePrice?.id || 'base'}`}>
                    <ListItem>
                      <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                        {primaryImage && (
                          <img
                            src={primaryImage.url}
                            alt={item.product.name}
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                          />
                        )}
                        <Box sx={{ flexGrow: 1 }}>
                          <ListItemText
                            primary={item.product.name}
                            secondary={
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  {item.selectedTimePrice ? `${item.selectedTimePrice.time} - ` : ''}
                                  {item.product.discountPercentage && (
                                    <span style={{ textDecoration: 'line-through', marginRight: 8 }}>
                                      {item.product.price}€
                                    </span>
                                  )}
                                  {price.toFixed(2)}€
                                </Typography>
                              </>
                            }
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleQuantityChange(item.product.id, e.target.value)}
                              inputProps={{ min: 1, style: { width: 60 } }}
                            />
                            <IconButton
                              onClick={() => removeItem(item.product.id)}
                              size="small"
                              color="error"
                            >
                              <TrashIcon size={18} />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography variant="subtitle1" sx={{ alignSelf: 'flex-start' }}>
                          {(price * item.quantity).toFixed(2)}€
                        </Typography>
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>

            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6">{getTotalPrice().toFixed(2)}€</Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                sx={{ mb: 2 }}
                onClick={handleCheckout}
              >
                Finalizar Compra
              </Button>

              <Button
                variant="outlined"
                fullWidth
                color="error"
                onClick={clearCart}
              >
                Vaciar Carrito
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default Cart;